import variant from '@jitl/quickjs-browser-release-sync-wasm';
import { newQuickJSWASMModuleFromVariant } from 'quickjs-emscripten-core';
import type { QuickJSWASMModule } from 'quickjs-emscripten-core';
import { env } from 'config/env';
import { logger } from 'modules/logger';

type Status = 'pending' | 'error' | 'success';

// ─── QuickJS WASM leak diagnostic (VX-4324) ──────────────────────────────────
// STRESS heap analysis found viewer/1's QuickJS WASM modules accumulating per
// case while viewer/0 stays flat. Static snapshots can't tell whether those
// modules are a true leak or only retained while the harness's CDP is attached.
// This probe lets the harness force a GC and observe whether the modules created
// in this page realm ever get finalized. Enabled in every deploy env except
// production (mirrors the SharedWorker status diagnostic), so prod carries zero
// cost: no registry, no counters, no globalThis exposure.
const DIAG_ENABLED = env.DEPLOY_ENV !== 'production';

interface QuickJsDiag {
  /** QuickJS WASM modules created in this realm (incremented on load). */
  created: number;
  /** Modules whose FinalizationRegistry callback has fired (i.e. collected). */
  finalized: number;
}

declare global {
  var __quickjsDiag: QuickJsDiag | undefined;
}

// Live under the flag only. Exposed on globalThis as the same object reference
// the counters mutate, so the harness's page.evaluate reads always see the
// current values without any plumbing.
const quickJsDiag: QuickJsDiag | null = DIAG_ENABLED ? { created: 0, finalized: 0 } : null;

const quickJsFinalizationRegistry =
  quickJsDiag != null && typeof FinalizationRegistry !== 'undefined'
    ? new FinalizationRegistry<void>(() => {
        quickJsDiag.finalized += 1;
        logger.info(
          `[diag] quickjs module finalized (created=${quickJsDiag.created} finalized=${quickJsDiag.finalized})`
        );
      })
    : null;

if (quickJsDiag != null) globalThis.__quickjsDiag = quickJsDiag;

const registerQuickJsModule = (module: QuickJSWASMModule): void => {
  if (quickJsDiag == null) return;
  quickJsDiag.created += 1;
  quickJsFinalizationRegistry?.register(module as object, undefined);
};

export const loadQuickJs = (): {
  read: () => QuickJSWASMModule;
} => {
  let status: Status = 'pending';
  let result: QuickJSWASMModule | Error | undefined;
  // Type assertion needed due to library type incompatibility between quickjs-emscripten dependencies
  const suspender = newQuickJSWASMModuleFromVariant(variant as any)
    .then((quickJs) => {
      status = 'success';
      result = quickJs;
      registerQuickJsModule(quickJs);
    })
    .catch((error) => {
      status = 'error';
      result = error;
    });

  return {
    read() {
      switch (status) {
        case 'pending':
          throw suspender;
        case 'error':
          throw result;
        case 'success':
          return result as QuickJSWASMModule;
        default:
          throw new Error(`Unknown status: ${status}`);
      }
    },
  };
};

// VX-4319: instantiate lazily on first use rather than eagerly at module import.
// The QuickJS module owns a ~17 MB WebAssembly.Memory and the library exposes no
// teardown, so eager init made every viewer realm that loaded the custom-annotation
// chunk allocate (and potentially strand) one even when no custom annotation ever
// rendered. Memoized so it stays a per-realm singleton once created.
let quickJsResource: ReturnType<typeof loadQuickJs> | undefined;

export function useQuickJS(): QuickJSWASMModule {
  quickJsResource ??= loadQuickJs();
  return quickJsResource.read();
}
